import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  ArrowLeft, RefreshCw, AlertTriangle, CheckCircle2, Clock, 
  MessageSquare, Calendar, Building2, Send, AlertCircle,
  PlayCircle, FileText
} from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

const PRIORITY_COLORS = {
  low: 'bg-green-100 text-green-800 border-green-300',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  critical: 'bg-red-100 text-red-800 border-red-300'
};

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  pending_review: 'bg-purple-100 text-purple-800',
  closed: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800'
};

const SubCorrectiveActions = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/sub/corrective-actions');
      setActions(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load corrective actions');
    } finally {
      setLoading(false);
    }
  };

  const openDetails = async (action) => {
    try {
      const response = await axiosInstance.get(`/gc/corrective-actions/${action.id}`);
      setSelectedAction(response.data);
      setShowDetailsDialog(true);
      setResponseText('');
    } catch (error) {
      toast.error('Failed to load action details');
    }
  };

  const handleSubmitResponse = async () => {
    if (!responseText.trim()) {
      toast.error('Please enter a response');
      return;
    }
    
    setSubmitting(true);
    try {
      await axiosInstance.post(`/sub/corrective-actions/${selectedAction.id}/respond`, {
        response: responseText,
        attachments: []
      });
      toast.success('Response submitted successfully');
      setResponseText('');
      
      // Refresh the action details
      const updated = await axiosInstance.get(`/gc/corrective-actions/${selectedAction.id}`);
      setSelectedAction(updated.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await axiosInstance.put(`/sub/corrective-actions/${selectedAction.id}/status?new_status=${newStatus}`);
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      
      // Refresh
      const updated = await axiosInstance.get(`/gc/corrective-actions/${selectedAction.id}`);
      setSelectedAction(updated.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isOverdue = (action) => {
    if (!action.dueDate || action.status === 'closed') return false;
    return new Date(action.dueDate) < new Date();
  };

  const pendingActions = actions.filter(a => ['open', 'in_progress'].includes(a.status));
  const reviewActions = actions.filter(a => a.status === 'pending_review');
  const closedActions = actions.filter(a => a.status === 'closed');

  const stats = {
    total: actions.length,
    pending: pendingActions.length,
    inReview: reviewActions.length,
    closed: closedActions.length,
    overdue: actions.filter(a => isOverdue(a)).length
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sub/my-projects')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Corrective Actions</h1>
              <p className="text-gray-600">Actions requested by General Contractors</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <FileText className="w-6 h-6 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-gray-500">Total</p>
            </CardContent>
          </Card>
          <Card className={stats.pending > 0 ? 'border-yellow-300 bg-yellow-50' : ''}>
            <CardContent className="p-4 text-center">
              <AlertCircle className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className="text-xs text-gray-500">Pending Action</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-purple-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{stats.inReview}</p>
              <p className="text-xs text-gray-500">In Review</p>
            </CardContent>
          </Card>
          <Card className={stats.overdue > 0 ? 'border-red-300 bg-red-50' : ''}>
            <CardContent className="p-4 text-center">
              <AlertTriangle className="w-6 h-6 text-red-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{stats.overdue}</p>
              <p className="text-xs text-gray-500">Overdue</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold">{stats.closed}</p>
              <p className="text-xs text-gray-500">Closed</p>
            </CardContent>
          </Card>
        </div>

        {/* Actions List */}
        <Card>
          <CardHeader>
            <CardTitle>Your Corrective Actions</CardTitle>
            <CardDescription>Click on an action to view details and respond</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="pending" className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Pending ({pendingActions.length})
                </TabsTrigger>
                <TabsTrigger value="review" className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  In Review ({reviewActions.length})
                </TabsTrigger>
                <TabsTrigger value="closed" className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Closed ({closedActions.length})
                </TabsTrigger>
              </TabsList>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : (
                <>
                  <TabsContent value="pending">
                    {pendingActions.length === 0 ? (
                      <div className="text-center py-12">
                        <CheckCircle2 className="w-12 h-12 text-green-300 mx-auto mb-4" />
                        <p className="text-gray-500">No pending corrective actions</p>
                      </div>
                    ) : (
                      <ActionTable actions={pendingActions} onRowClick={openDetails} isOverdue={isOverdue} formatDate={formatDate} />
                    )}
                  </TabsContent>
                  
                  <TabsContent value="review">
                    {reviewActions.length === 0 ? (
                      <div className="text-center py-12">
                        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No actions pending GC review</p>
                      </div>
                    ) : (
                      <ActionTable actions={reviewActions} onRowClick={openDetails} isOverdue={isOverdue} formatDate={formatDate} />
                    )}
                  </TabsContent>
                  
                  <TabsContent value="closed">
                    {closedActions.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No closed actions yet</p>
                      </div>
                    ) : (
                      <ActionTable actions={closedActions} onRowClick={openDetails} isOverdue={isOverdue} formatDate={formatDate} />
                    )}
                  </TabsContent>
                </>
              )}
            </Tabs>
          </CardContent>
        </Card>

        {/* Action Details Dialog */}
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedAction && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <DialogTitle>{selectedAction.title}</DialogTitle>
                      <DialogDescription>
                        From: {selectedAction.gcCompanyName} • Project: {selectedAction.projectName}
                      </DialogDescription>
                    </div>
                    <div className="flex gap-2">
                      <Badge className={PRIORITY_COLORS[selectedAction.priority]}>
                        {selectedAction.priority}
                      </Badge>
                      <Badge className={isOverdue(selectedAction) ? STATUS_COLORS.overdue : STATUS_COLORS[selectedAction.status]}>
                        {isOverdue(selectedAction) ? 'Overdue' : selectedAction.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Due Date Warning */}
                  {isOverdue(selectedAction) && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <span className="text-red-700 font-medium">This action is overdue! Due date was {formatDate(selectedAction.dueDate)}</span>
                    </div>
                  )}
                  
                  {/* Details */}
                  <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">Created</p>
                      <p className="font-medium">{formatDate(selectedAction.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Due Date</p>
                      <p className={`font-medium ${isOverdue(selectedAction) ? 'text-red-600' : ''}`}>
                        {formatDate(selectedAction.dueDate)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <div>
                    <h4 className="font-medium mb-2">GC's Request</h4>
                    <p className="text-gray-700 whitespace-pre-wrap p-3 bg-orange-50 rounded-lg border border-orange-100">
                      {selectedAction.description}
                    </p>
                  </div>
                  
                  {/* Status Update Buttons */}
                  {selectedAction.status === 'open' && (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => handleUpdateStatus('in_progress')}
                        className="flex-1"
                      >
                        <PlayCircle className="w-4 h-4 mr-2" />
                        Mark In Progress
                      </Button>
                    </div>
                  )}
                  
                  {/* Previous Responses */}
                  {selectedAction.responses?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Your Responses ({selectedAction.responses.length})
                      </h4>
                      <div className="space-y-3">
                        {selectedAction.responses.map((response, idx) => (
                          <div key={response.id || idx} className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-sm">{response.respondedByName}</span>
                              <span className="text-xs text-gray-500">{formatDate(response.respondedAt)}</span>
                            </div>
                            <p className="text-gray-700">{response.response}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Submit Response */}
                  {selectedAction.status !== 'closed' && (
                    <div className="pt-4 border-t">
                      <h4 className="font-medium mb-2">Submit Response</h4>
                      <Textarea
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        placeholder="Describe the corrective actions taken, what was fixed, how it was addressed..."
                        rows={4}
                        className="mb-3"
                      />
                      <Button 
                        onClick={handleSubmitResponse} 
                        disabled={submitting || !responseText.trim()}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {submitting ? 'Submitting...' : 'Submit Response'}
                      </Button>
                    </div>
                  )}
                  
                  {/* Resolution Notes */}
                  {selectedAction.status === 'closed' && (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-medium text-green-800 mb-1 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Action Closed
                      </h4>
                      {selectedAction.resolutionNotes && (
                        <p className="text-green-700">{selectedAction.resolutionNotes}</p>
                      )}
                      <p className="text-xs text-green-600 mt-2">Closed on {formatDate(selectedAction.closedAt)}</p>
                    </div>
                  )}
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>Close</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Reusable Action Table Component
const ActionTable = ({ actions, onRowClick, isOverdue, formatDate }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>Title</TableHead>
        <TableHead>From (GC)</TableHead>
        <TableHead>Project</TableHead>
        <TableHead>Priority</TableHead>
        <TableHead>Due Date</TableHead>
        <TableHead>Responses</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {actions.map((action) => (
        <TableRow 
          key={action.id} 
          className="cursor-pointer hover:bg-gray-50"
          onClick={() => onRowClick(action)}
        >
          <TableCell className="font-medium">{action.title}</TableCell>
          <TableCell>{action.gcCompanyName}</TableCell>
          <TableCell>{action.projectName}</TableCell>
          <TableCell>
            <Badge className={PRIORITY_COLORS[action.priority]}>
              {action.priority}
            </Badge>
          </TableCell>
          <TableCell className={isOverdue(action) ? 'text-red-600 font-medium' : ''}>
            {isOverdue(action) && <AlertTriangle className="w-4 h-4 inline mr-1" />}
            {formatDate(action.dueDate)}
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4 text-gray-400" />
              <span>{action.responses?.length || 0}</span>
            </div>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export default SubCorrectiveActions;
